import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function NeedsList() {
  const navigate = useNavigate();

  const printPage = () => {
    window.print();
    toast.success('Print dialog opened');
  };

  const needsCategories = [
    {
      title: 'Sustenance',
      needs: ['air', 'food', 'health & healing', 'pleasure', 'shelter', 'touch', 'water']
    },
    {
      title: 'Safety',
      needs: ['consistency', 'order', 'predictability', 'protection', 'reassurance', 'security', 'safety (emotional)', 'safety (physical)', 'stability', 'structure']
    },
    {
      title: 'Love',
      needs: ['acceptance', 'affection', 'attention', 'belonging', 'care', 'closeness', 'companionship', 'connection', 'friendship', 'harmony', 'honesty', 'intimacy', 'nurturing', 'sexual expression', 'to hear/be heard', 'to trust/be trusted', 'warmth']
    },
    {
      title: 'Empathy',
      needs: ['authenticity', 'compassion', 'integrity', 'presence', 'to be heard', 'to understand', 'to be understood']
    },
    {
      title: 'Rest, Recreation & Play',
      needs: ['beauty', 'exercise', 'humor', 'joy', 'leisure', 'movement', 'quiet', 'relaxation', 'rest', 'sleep', 'solitude']
    },
    {
      title: 'Community',
      needs: ['acknowledgement', 'belonging', 'collaboration', 'communication', 'communion', 'consideration', 'cooperation', 'ease', 'equality', 'fairness', 'feedback', 'inclusion', 'mutuality', 'recognition']
    },
    {
      title: 'Creativity',
      needs: ['clarity', 'discovery', 'effectiveness', 'efficiency', 'exploration', 'flow', 'focus', 'information', 'inspiration', 'integration', 'knowledge', 'power in my world', 'rejuvenation', 'self-expression', 'stimulation']
    },
    {
      title: 'Autonomy',
      needs: ['authenticity', 'choice', 'freedom', 'independence', 'space', 'spaciousness', 'spontaneity', 'wholeness']
    },
    {
      title: 'Meaning or Purpose in Life',
      needs: ['aliveness', 'awareness', 'celebration of life', 'challenge', 'competence', 'consciousness', 'contribution', 'growth', 'hope', 'learning', 'making sense of life', 'mourning', 'nature', 'participation', 'peace (external)', 'peace (internal)', 'self-acceptance', 'self-care', 'self-connection', 'self-knowledge', 'self-love', 'self-realization', 'self-responsibility', 'to matter', 'to understand', 'to be understood', 'transcendence', 'well-being of someone else']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">Needs List</h1>
                <p className="text-slate-300 text-sm">The 9 Basic Human Needs</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 5</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Attribution */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6 mb-8">
            <p className="text-slate-200 text-center italic">
              By Manfred Max-Neef, Chilean economist and environmentalist
            </p>
          </div>

          {/* 9 Basic Needs Summary */}
          <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">The 9 Basic Human Needs</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {needsCategories.map((category, idx) => (
                <div
                  key={idx}
                  className="bg-slate-800/40 rounded-lg p-4 border border-slate-700/30 hover:border-slate-600/50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-white mb-1">{idx + 1}. {category.title}</h3>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Needs by Category */}
          <div className="space-y-6">
            {needsCategories.map((category, idx) => (
              <div
                key={idx}
                className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden"
                data-testid={`needs-category-${idx}`}
              >
                <div className="bg-slate-800/60 px-6 py-4 border-b border-slate-700/30">
                  <h3 className="text-xl font-bold text-white">{category.title}</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {category.needs.map((need, needIdx) => (
                      <div
                        key={needIdx}
                        className="bg-slate-900/40 px-4 py-2 rounded border border-slate-700/30 hover:border-slate-600/50 hover:bg-slate-800/40 transition-colors"
                      >
                        <span className="text-slate-200 text-sm">{need}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* AI Practice Scenario */}
          <AIPracticeScenario pageTitle="Needs List" pageNumber={5} />

          {/* Page Messaging */}
          <PageMessaging pageTitle="Needs List" pageNumber={5} />
        </div>
      </div>
    </div>
  );
}
